import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { DollarSign, Calendar, FileText, Download, X, Printer } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const generateInvoiceHTML = (data, payment, treatment, lang, t) => {
  const isRTL = lang === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';
  const align = isRTL ? 'right' : 'left';
  const alignOpp = isRTL ? 'left' : 'right';
  const statusLabel = { paid: t.paid, pending: t.pending, overdue: t.overdue }[payment.status] || payment.status;
  const statusColor = { paid: '#059669', pending: '#d97706', overdue: '#dc2626' }[payment.status] || '#6b7280';
  const dateStr = new Date(payment.date).toLocaleDateString(lang === 'ar' ? 'ar-EG' : lang === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const treatmentCost = treatment ? treatment.cost : payment.amount;
  const remaining = Math.max(treatmentCost - payment.amount, 0);

  return `<!DOCTYPE html>
<html lang="${lang}" dir="${dir}">
<head>
<meta charset="UTF-8">
<title>${t.invoice} - ${payment.invoice_number}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&family=Inter:wght@400;600;700&display=swap');
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: ${isRTL ? "'Cairo'" : "'Inter'"}, sans-serif; color: #1f2937; background: #fff; direction: ${dir}; }
  .invoice { max-width: 800px; margin: 0 auto; background: #fff; }
  .header { background: #0F4C5C; color: #fff; padding: 40px; display: flex; justify-content: space-between; align-items: flex-start; }
  .header h1 { font-size: 26px; font-weight: 700; margin-bottom: 6px; }
  .header p { font-size: 13px; opacity: 0.85; }
  .invoice-badge { background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.3); padding: 8px 20px; border-radius: 6px; text-align: center; }
  .invoice-badge span { display: block; font-size: 11px; opacity: 0.8; text-transform: uppercase; letter-spacing: 1px; }
  .invoice-badge strong { font-size: 18px; }
  .body { padding: 40px; }
  .meta-row { display: flex; justify-content: space-between; margin-bottom: 32px; gap: 24px; flex-wrap: wrap; }
  .meta-box { flex: 1; min-width: 200px; }
  .meta-box h3 { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #6b7280; margin-bottom: 8px; font-weight: 600; }
  .meta-box p { font-size: 15px; color: #1f2937; line-height: 1.6; }
  .divider { border: none; border-top: 1px solid #e5e7eb; margin: 24px 0; }
  table { width: 100%; border-collapse: collapse; margin: 24px 0; }
  th { background: #f9fafb; padding: 14px 16px; text-align: ${align}; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; color: #6b7280; font-weight: 600; border-bottom: 2px solid #e5e7eb; }
  td { padding: 16px; font-size: 14px; border-bottom: 1px solid #f3f4f6; }
  .amount-col { text-align: ${alignOpp}; font-weight: 700; font-size: 16px; }
  .status-badge { display: inline-block; padding: 4px 14px; border-radius: 20px; font-size: 12px; font-weight: 600; color: #fff; background: ${statusColor}; }
  .totals { border-radius: 8px; overflow: hidden; border: 1px solid #e5e7eb; margin-top: 24px; }
  .totals .row { display: flex; justify-content: space-between; align-items: center; padding: 16px 24px; border-bottom: 1px solid #e5e7eb; }
  .totals .row:last-child { border-bottom: none; }
  .footer { background: #f9fafb; border-top: 1px solid #e5e7eb; padding: 24px 40px; text-align: center; }
  .footer p { font-size: 12px; color: #9ca3af; line-height: 1.8; }
  .footer strong { color: #6b7280; }
  @media print { body { background: #fff; } }
</style>
</head>
<body>
<div class="invoice">
  <div class="header">
    <div>
      <h1>${t.clinicName}</h1>
      <p>70059031 | info@younesdental.com</p>
    </div>
    <div class="invoice-badge">
      <span>${t.invoice}</span>
      <strong>${payment.invoice_number}</strong>
    </div>
  </div>
  <div class="body">
    <div class="meta-row">
      <div class="meta-box">
        <h3>${t.patient || 'Patient'}</h3>
        <p><strong>${data.patient_name}</strong></p>
        <p>${data.patient_email}</p>
        ${data.patient_phone ? `<p>${data.patient_phone}</p>` : ''}
      </div>
      <div class="meta-box" style="text-align:${alignOpp}">
        <h3>${t.date}</h3>
        <p>${dateStr}</p>
        <h3 style="margin-top:12px">${t.status}</h3>
        <p><span class="status-badge">${statusLabel}</span></p>
      </div>
    </div>
    <hr class="divider">
    <table>
      <thead>
        <tr>
          <th>${t.description}</th>
          <th>${t.doctor || 'Doctor'}</th>
          <th>${t.teeth || 'Teeth'}</th>
          <th style="text-align:${alignOpp}">${t.cost || 'Cost'}</th>
        </tr>
      </thead>
      <tbody>
        ${treatment ? `
        <tr>
          <td><strong>${treatment.treatment_type || payment.description}</strong>${treatment.notes ? `<br><span style="color:#6b7280;font-size:12px">${treatment.notes}</span>` : ''}</td>
          <td>${treatment.doctor || '-'}</td>
          <td>${treatment.teeth && treatment.teeth.length > 0 ? treatment.teeth.join(', ') : '-'}</td>
          <td class="amount-col">$${treatmentCost.toFixed(2)}</td>
        </tr>` : `
        <tr>
          <td><strong>${payment.description}</strong></td>
          <td>-</td>
          <td>-</td>
          <td class="amount-col">$${payment.amount.toFixed(2)}</td>
        </tr>`}
      </tbody>
    </table>
    <div class="totals">
      <div class="row" style="background:#f9fafb;">
        <span style="font-size:14px;color:#6b7280;">${isRTL ? 'المبلغ الإجمالي' : lang === 'fr' ? 'Montant total' : 'Total Amount'}</span>
        <span style="font-size:20px;font-weight:700;color:#1f2937;">$${treatmentCost.toFixed(2)}</span>
      </div>
      <div class="row" style="background:#f0fdf4;">
        <span style="font-size:14px;color:#059669;font-weight:600;">${isRTL ? 'المبلغ المدفوع' : lang === 'fr' ? 'Montant payé' : 'Paid'}</span>
        <span style="font-size:20px;font-weight:700;color:#059669;">$${payment.amount.toFixed(2)}</span>
      </div>
      <div class="row" style="background:${remaining > 0 ? '#fef2f2' : '#f9fafb'};">
        <span style="font-size:14px;color:${remaining > 0 ? '#dc2626' : '#6b7280'};font-weight:600;">${isRTL ? 'المبلغ المتبقي' : lang === 'fr' ? 'Restant' : 'Remaining'}</span>
        <span style="font-size:20px;font-weight:700;color:${remaining > 0 ? '#dc2626' : '#059669'};">$${remaining.toFixed(2)}</span>
      </div>
    </div>
  </div>
  <div class="footer">
    <p><strong>${t.clinicName}</strong></p>
    <p>70059031 | info@younesdental.com</p>
  </div>
</div>
</body>
</html>`;
};

const PaymentRecords = () => {
  const { token } = useAuth();
  const { lang, t } = useLanguage();
  const [payments, setPayments] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoiceHTML, setInvoiceHTML] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [pRes, tRes] = await Promise.all([axios.get(`${API}/patients/payments`, { headers }), axios.get(`${API}/patients/treatments`, { headers })]);
      setPayments(pRes.data); setTreatments(tRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const downloadInvoice = async (payment) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/patients/invoice/${payment.id}`, { headers });
      const data = res.data;
      let treatment = data.treatment;
      if (!treatment && payment.treatment_id) {
        treatment = treatments.find(tr => tr.id === payment.treatment_id);
      }
      const html = generateInvoiceHTML(data, payment, treatment, lang, t);
      setInvoiceHTML(html);
    } catch (e) { toast.error('Failed to generate invoice'); }
  };

  const handlePrint = () => {
    const iframe = iframeRef.current;
    if (iframe) {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }
  };

  const getStatusBadge = (status) => {
    const styles = { paid: 'bg-emerald-100 text-emerald-700', pending: 'bg-amber-100 text-amber-700', overdue: 'bg-red-100 text-red-700' };
    return `${styles[status] || styles.pending} px-2.5 py-1 rounded-full text-xs font-semibold`;
  };
  const statusText = (s) => ({ paid: t.paid, pending: t.pending, overdue: t.overdue }[s] || s);

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F4C5C]"></div></div>;

  const totalTreatmentCost = treatments.reduce((sum, tr) => sum + (parseFloat(tr.cost) || 0), 0);
  const totalPaid = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const pendingAmount = payments.filter(p => p.status !== 'paid').reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const treatmentBalance = Math.max(totalTreatmentCost - totalPaid, 0);
  const outstandingBalance = Math.max(treatmentBalance, pendingAmount);

  return (
    <div className="p-6 lg:p-8" data-testid="payment-records">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{t.paymentRecords}</h1>
        <p className="text-[#6B7280]">{t.paymentRecordsSubtitle}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-2"><span className="text-sm text-[#6B7280]">{t.totalPaid}</span><DollarSign className="w-5 h-5 text-green-600" /></div>
          <div className="text-3xl font-bold text-[#1F2937]" data-testid="total-paid">${totalPaid.toFixed(2)}</div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-2"><span className="text-sm text-[#6B7280]">{t.outstandingBalance}</span><DollarSign className="w-5 h-5 text-orange-600" /></div>
          <div className="text-3xl font-bold text-[#1F2937]" data-testid="outstanding-balance">${outstandingBalance.toFixed(2)}</div>
        </div>
      </div>

      {payments.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center" data-testid="no-payments">
          <FileText className="w-16 h-16 text-[#6B7280] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-[#1F2937] mb-2">{t.noPaymentRecords}</h3>
          <p className="text-[#6B7280]">{t.noPaymentRecordsDesc}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="payments-table">
              <thead className="bg-[#F9FAFB] border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.invoiceNum}</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.date}</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.description}</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-[#1F2937]">{t.status}</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-[#1F2937]">{t.amount}</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-[#1F2937]">{t.invoice}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {payments.map((payment, index) => (
                  <tr key={payment.id} className="hover:bg-[#F9FAFB] transition-colors" data-testid={`payment-row-${index}`}>
                    <td className="px-6 py-4"><span className="text-sm font-mono text-[#1F2937]">{payment.invoice_number}</span></td>
                    <td className="px-6 py-4"><div className="flex items-center text-sm"><Calendar className="w-4 h-4 mr-2 text-[#6B7280]" /><span className="text-[#1F2937]">{new Date(payment.date).toLocaleDateString()}</span></div></td>
                    <td className="px-6 py-4"><span className="text-sm text-[#1F2937]">{payment.description}</span></td>
                    <td className="px-6 py-4"><span className={getStatusBadge(payment.status)} data-testid={`payment-status-${index}`}>{statusText(payment.status)}</span></td>
                    <td className="px-6 py-4 text-right"><span className="text-sm font-semibold text-[#1F2937]">${payment.amount.toFixed(2)}</span></td>
                    <td className="px-6 py-4 text-center">
                      <button onClick={() => downloadInvoice(payment)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[#0F4C5C] hover:bg-[#0F4C5C]/10 rounded-lg transition-colors text-sm font-medium" data-testid={`download-invoice-${index}`}>
                        <Download className="w-4 h-4" /><span className="hidden sm:inline">{t.download}</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoice Preview Modal */}
      {invoiceHTML && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="invoice-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-[#1F2937]">{t.invoice}</h2>
              <div className="flex items-center gap-2">
                <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 bg-[#0F4C5C] text-white rounded-lg hover:bg-[#0F4C5C]/90 text-sm font-medium transition-colors" data-testid="print-invoice-btn">
                  <Printer className="w-4 h-4" /> Print / Save PDF
                </button>
                <button onClick={() => setInvoiceHTML(null)} className="p-2 text-[#6B7280] hover:text-[#1F2937] rounded-lg hover:bg-slate-100 transition-colors" data-testid="close-invoice-btn">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <iframe
                ref={iframeRef}
                srcDoc={invoiceHTML}
                title="Invoice"
                className="w-full h-full min-h-[600px] border-0"
                data-testid="invoice-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentRecords;
